import {Distance} from '../../Domain/Distance';
import {Stroke} from '../../Models/Stroke';
import {getXAxisUnitFormatter, getXAxisUnitInnerProperty, XAxisUnit} from './variableXAxis';

type GraphableStroke = Stroke & {fullWorkoutTotalDistance: Distance};
type GraphAxisData = {
    x: Array<{value: number; label: string}>;
    y: Array<{value: number; label: string}>;
};
type GraphValues = Array<{x: number; y: number}>;

export const lazyTake = (object: any, propertyPath: string): any => {
    let value = object;
    propertyPath.split('.').forEach((property) => {
        if (value.hasOwnProperty(property)) {
            value = value[property];
        } else {
            throw new Error(`Property ${propertyPath} of ${JSON.stringify(object, null, 2)} not found.`);
        }
    });
    return value;
};

export const strokesToGraphableStrokes = (strokes: Stroke[]): GraphableStroke[] => {
    return strokes.reduce((graphableStrokes: GraphableStroke[], stroke: Stroke) => {
        const totalDistance =
            graphableStrokes.length > 0 && stroke.distancePerStroke
                ? graphableStrokes[graphableStrokes.length - 1].fullWorkoutTotalDistance.add(stroke.distancePerStroke)
                : Distance.zero();
        graphableStrokes.push({...stroke, fullWorkoutTotalDistance: totalDistance});
        return graphableStrokes;
    }, []);
};

export const strokesToGraphData = (
    strokes: GraphableStroke[],
    xAxisUnit: XAxisUnit,
    yValueCallback: (stroke: GraphableStroke) => number,
    yFormattingCallback: (property: number) => string,
): {axis: GraphAxisData; values: GraphValues} => {
    const graphValues = strokes.map((stroke) => {
        return {x: lazyTake(stroke, getXAxisUnitInnerProperty(xAxisUnit)), y: yValueCallback(stroke)};
    });
    const axisData = axisDataFromGraphValues(graphValues, getXAxisUnitFormatter(xAxisUnit), yFormattingCallback);
    const normalizedValues = normalizeGraphValues(graphValues);
    return {axis: axisData, values: normalizedValues};
};

const normalizeGraphValues = (graphValues: GraphValues): GraphValues => {
    const maxSpeedBuffer: number = 0.2; // Should be replaced with a relative value, but works for now.
    const yValues: number[] = graphValues.map((d) => d.y);
    const yMin: number = Math.min.apply(0, yValues);
    const yMax: number = Math.max.apply(0, yValues);
    const yScale: number = 1 / (yMax + maxSpeedBuffer - yMin);
    return graphValues.map((d) => {
        return {x: d.x, y: d.y < 0 ? 0 : (d.y - yMin) * yScale};
    });
};

const axisDataFromGraphValues = (
    graphValues: GraphValues,
    xFormatting: (value: number) => string,
    yFormatting: (value: number) => string,
): GraphAxisData => {
    const maxSpeedBuffer: number = 0.2; // Should be replaced with a relative value, but works for now.

    // Calculate the value ranges.
    const xValues = graphValues.map((d) => d.x);
    const yValues = graphValues.map((d) => d.y);
    const xMin = Math.min.apply(0, xValues);
    const xMax = Math.max.apply(0, xValues);
    const yMin = Math.min.apply(0, yValues);
    const yMax = Math.max.apply(0, yValues) + maxSpeedBuffer;
    const xStep = (xMax - xMin) / 7;

    // Create a list of values and labels that fit the graph.
    const axisData: GraphAxisData = {x: [], y: []};
    for (let index = 0; index < 8; index++) {
        const xValue: number = xMin + xStep * index;
        const yValue: number = yMin + (yMax - yMin) * (1 - index / 8);
        axisData.x.push({value: xValue, label: index % 7 === 0 ? '' : xFormatting(xValue)});
        axisData.y.push({value: (8 - index) / 8, label: index % 2 === 0 ? '' : yFormatting(yValue)});
    }
    return axisData;
};
